import {
    mdiAt,
    mdiEmailArrowRightOutline,
    mdiEmailOutline,
    mdiFax,
    mdiFileDocumentOutline,
    mdiPhoneIncoming,
    mdiPhoneOutgoing,
    mdiPhoneOutline,
    mdiSubdirectoryArrowRight,
} from '@mdi/js';
import Icon from '@mdi/react';
import { observer } from 'mobx-react';
import React from 'react';
import { fileCabinetDocumentTypeLabel } from '../../services/file-cabinet/file-cabinet.document-types';
import type { FileCabinetReference } from '../../services/file-cabinet/file-cabinet.references';
import type {
    CabinetCall,
    CabinetDocument,
    CabinetEmail,
    CabinetLetter,
    CabinetMail,
    CabinetReceivedFax,
    CabinetSentFax,
    FileCabinetRecord,
    FileCabinetRecordByView,
    FileCabinetViewKey,
} from '../../services/file-cabinet/file-cabinet.types';
import type { FileCabinetCallColumnKey, FileCabinetColumnKeys } from '../../services/file-cabinet/file-cabinet.views';
import type { FileCabinetAttachments } from '../../stores/file-cabinet/file-cabinet.store';
import { callDuration, claimsOf, hasTime, letterStatus, methodLabel } from '../../services/file-cabinet/file-cabinet.records';
import { formatBytes } from '../../services/primitives/number-format';
import { formatDate } from '../../services/primitives/date-utils';
import { formatPhoneNumber } from '../claim-details/cards/cards';
import * as S from './file-cabinet.styles';
import type { ChipTone } from './file-cabinet.styles';

/** The shared atoms of the File Cabinet table — the design's `Two`, `When`, `ClaimCell` and tags. */

export const ABSENT = '—';

export function Absent(): React.ReactElement {
    return <S.Absent>{ABSENT}</S.Absent>;
}

export function TwoLine({
    primary,
    secondary,
    strong,
    mono,
}: {
    primary: React.ReactNode;
    secondary?: React.ReactNode;
    strong?: boolean;
    mono?: boolean;
}): React.ReactElement {
    if (primary === undefined || primary === null || primary === '') {
        return <Absent />;
    }
    return (
        <S.Two>
            <S.Primary strong={strong} mono={mono}>
                {primary}
            </S.Primary>
            {secondary && <S.Secondary>{secondary}</S.Secondary>}
        </S.Two>
    );
}

/** Date over time, in the viewer's zone. An absent date is a dash, not "Invalid date". */
export function When({ value }: { value?: string }): React.ReactElement {
    if (!value) {
        return <Absent />;
    }
    if (!hasTime(value)) {
        return <TwoLine primary={formatDate(value, 'MMM d, yyyy')} />;
    }
    return <TwoLine primary={formatDate(value, 'MMM d, yyyy')} secondary={formatDate(value, 'h:mm a')} />;
}

export function Muted({ children }: { children?: React.ReactNode }): React.ReactElement {
    if (children === undefined || children === null || children === '') {
        return <Absent />;
    }
    return <S.Muted>{children}</S.Muted>;
}

/** `+18006241234` → `(800) 624-1234`; anything that is not a US number is shown as sent. */
export function phone(e164?: string): string {
    if (!e164) {
        return '';
    }
    // Only a US number (+1 and ten digits) gets the US format. The shared formatter strips every
    // non-digit and formats any ten digits, which would show `+39 06 1234 5678` as a US number.
    return /^\+1\d{10}$/.test(e164) ? formatPhoneNumber(e164) ?? e164 : e164;
}

export function DocumentTypeTag({ type }: { type?: string }): React.ReactElement {
    if (!type) {
        return <Absent />;
    }
    return <S.Chip tone="neutral">{fileCabinetDocumentTypeLabel(type)}</S.Chip>;
}

/**
 * The first claim, shortened, with how many more — every id in the tooltip. "No claim" only when the
 * record says so: every document on sandbox carried `claimIds`, empty or not (100 of 100, 2026-10-22).
 * Undefined — a record that came without its document — is unknown, shown as a dash (NS-3).
 */
export function ClaimCell({ ids }: { ids: string[] | undefined }): React.ReactElement {
    if (ids === undefined) {
        return <Absent />;
    }
    if (ids.length === 0) {
        return <S.Absent>No claim</S.Absent>;
    }
    return (
        <S.Two title={ids.join('\n')}>
            <S.ClaimId>{ids[0].slice(0, 8)}…</S.ClaimId>
            {ids.length > 1 && (
                <S.Secondary>
                    +{ids.length - 1} more claim{ids.length > 2 ? 's' : ''}
                </S.Secondary>
            )}
        </S.Two>
    );
}

/** Under a document's name: where it came from, else its description, else its size. */
function documentSubline(document: CabinetDocument): React.ReactNode {
    const sources = document.sourceDocumentIds ?? [];
    if (sources.length) {
        return (
            <S.LineageNote>
                <Icon path={mdiSubdirectoryArrowRight} size={0.55} />
                From {sources.length === 1 ? '1 source document' : `${sources.length} source documents`}
            </S.LineageNote>
        );
    }
    if (document.description) {
        return document.description;
    }
    return document.filesize ? formatBytes(document.filesize) : undefined;
}

/** What a cell can read beyond its own record. */
export interface FileCabinetCellContext {
    /**
     * All Documents: what carries each document on the page. A getter, read inside an observer, so the
     * cell updates when the attachments arrive after the page.
     */
    attachments?: () => FileCabinetAttachments | undefined;
    /** Opens a row's record in the drawer — the keyboard's way in (the row click is the mouse's). */
    open?: (record: FileCabinetRecord) => void;
}

type Renderer<V extends FileCabinetViewKey> = (record: FileCabinetRecordByView[V], context: FileCabinetCellContext) => React.ReactElement;

/** Every column of every view, by key — the compiler checks the keys against `FileCabinetColumnKeys`. */
type Renderers = { [V in FileCabinetViewKey]: Record<FileCabinetColumnKeys[V], Renderer<V>> };

const claimsCell =
    <V extends FileCabinetViewKey>(view: V): Renderer<V> =>
    (record): React.ReactElement =>
        <ClaimCell ids={claimsOf(view, record)} />;

/** The three call tables share columns; a plain type, as the generic one is compared by its parameter. */
type CallRenderers = Record<FileCabinetCallColumnKey, (call: CabinetCall, context: FileCabinetCellContext) => React.ReactElement>;

const CALL_CELLS = (view: 'call.in' | 'call.out' | 'call.other'): CallRenderers => ({
    started: call => <When value={call.start} />,
    number: call => <TwoLine strong primary={phone(call.phoneE164)} secondary={methodLabel(call.method)} />,
    agent: call => <Muted>{call.agent}</Muted>,
    duration: call => <Muted>{callDuration(call.start, call.end)}</Muted>,
    // A missing `document` says no recording came with the call — not that none was made (NS-3).
    recording: call => (call.document ? <S.Chip tone="neutral">Recording</S.Chip> : <Absent />),
    claims: call => <ClaimCell ids={claimsOf(view, call)} />,
    created: call => <When value={call.created} />,
});

const RENDERERS: Renderers = {
    docs: {
        // A document can have no filename; the drawer calls it the same.
        document: document => (
            // No filename is said as such — muted, not dressed up as a name the filename search could find.
            <TwoLine strong primary={document.filename || <S.Absent>No filename</S.Absent>} secondary={documentSubline(document)} />
        ),
        type: document => <DocumentTypeTag type={document.documentType} />,
        attached: (document, context) => <AttachedCell documentId={document.id} getAttachments={context.attachments} />,
        claims: claimsCell('docs'),
        created: document => <When value={document.created} />,
    },
    'mail.in': {
        received: mail => <When value={mail.receivedDate} />,
        barcode: mail => <TwoLine mono primary={mail.barcode} secondary={[mail.itemType, mail.type].filter(Boolean).join(' · ')} />,
        pages: mail => <Muted>{mail.pages}</Muted>,
        // 0 is a count, not an absence: the drawer shows it too.
        checks: mail =>
            mail.checks ? (
                <S.Chip tone="success">
                    {mail.checks} check{mail.checks > 1 ? 's' : ''}
                </S.Chip>
            ) : (
                <Muted>{mail.checks}</Muted>
            ),
        scanned: mail => <When value={mail.scanDate} />,
        file: mail => <Muted>{mail.document?.filename}</Muted>,
        claims: claimsCell('mail.in'),
    },
    'mail.out': {
        created: letter => <When value={letter.created} />,
        recipient: letter => (
            <TwoLine
                strong
                primary={letter.response?.to?.company || letter.response?.to?.name}
                secondary={[letter.response?.to?.city, letter.response?.to?.state].filter(Boolean).join(', ')}
            />
        ),
        service: letter => (
            <TwoLine primary={letter.response?.mailType} secondary={letter.response?.extraService || 'No extra service'} />
        ),
        lastEvent: letter => <S.Chip tone="neutral">{letterStatus(letter)}</S.Chip>,
        expected: letter => (
            <Muted>
                {letter.response?.expectedDeliveryDate ? formatDate(letter.response.expectedDeliveryDate, 'MMM d, yyyy') : undefined}
            </Muted>
        ),
        claims: claimsCell('mail.out'),
    },
    'fax.in': {
        received: fax => <When value={fax.received} />,
        from: fax => <TwoLine strong primary={phone(fax.fromE164)} secondary={fax.toE164 ? `To ${phone(fax.toE164)}` : undefined} />,
        pages: fax => <Muted>{fax.pages}</Muted>,
        file: fax => <Muted>{fax.document?.filename}</Muted>,
        claims: claimsCell('fax.in'),
        created: fax => <When value={fax.created} />,
    },
    'fax.out': {
        sent: fax => <When value={fax.sent} />,
        to: fax => <TwoLine strong primary={phone(fax.toE164)} secondary={fax.fromE164 ? `From ${phone(fax.fromE164)}` : undefined} />,
        pages: fax => <Muted>{fax.pages}</Muted>,
        result: fax => <FaxResult fax={fax} />,
        file: fax => <Muted>{fax.document?.filename}</Muted>,
        claims: claimsCell('fax.out'),
        created: fax => <When value={fax.created} />,
    },
    'email.out': {
        sent: email => <When value={email.created} />,
        to: email => <TwoLine strong primary={email.toName || email.toEmail} secondary={email.toName ? email.toEmail : undefined} />,
        subject: email => (
            <TwoLine
                primary={email.subject || '(no subject)'}
                secondary={email.document ? email.document.filename || 'Untitled attachment' : 'No attachment'}
            />
        ),
        status: email => <EmailStatus status={email.status} />,
        claims: claimsCell('email.out'),
    },
    'call.in': CALL_CELLS('call.in'),
    'call.out': CALL_CELLS('call.out'),
    'call.other': CALL_CELLS('call.other'),
    'call.ai': {
        created: call => <When value={call.created} />,
        number: call => <TwoLine strong primary={phone(call.number)} secondary="AI call" />,
        script: call => (call.scriptId ? <S.Mono title="The API returns the script's ID, not its name">{call.scriptId}</S.Mono> : <Absent />),
        // A free string in the API: shown as sent, never coloured as if it meant success or failure (NS-4).
        status: call => (call.status ? <S.Chip tone="neutral">{call.status}</S.Chip> : <Absent />),
        completed: call => <When value={call.completedDate} />,
    },
};

/** One cell, by view and column key. */
export function renderFileCabinetCell<V extends FileCabinetViewKey>(
    view: V,
    key: FileCabinetColumnKeys[V],
    record: FileCabinetRecordByView[V],
    context: FileCabinetCellContext,
): React.ReactElement {
    const renderers = RENDERERS[view] as Record<FileCabinetColumnKeys[V], Renderer<V>>;
    return renderers[key](record, context);
}

/**
 * `EmailStatusType` is an enum, so it may be coloured by meaning — the one status in the File Cabinet
 * that is (API Coverage). Opened and delivered are good news, bounced is bad, sent is not yet either.
 */
export function EmailStatus({ status }: { status?: string }): React.ReactElement {
    if (!status) {
        return <Absent />;
    }
    const tone: Record<string, ChipTone> = { OPENED: 'success', DELIVERED: 'primary', SENT: 'neutral', BOUNCED: 'danger' };
    return <S.Chip tone={tone[status] ?? 'neutral'}>{methodLabel(status)}</S.Chip>;
}

const REFERENCE_ICONS: Partial<Record<FileCabinetViewKey, string>> = {
    'mail.in': mdiEmailOutline,
    'mail.out': mdiEmailArrowRightOutline,
    'fax.in': mdiFax,
    'fax.out': mdiFax,
    'email.out': mdiAt,
    'call.in': mdiPhoneIncoming,
    'call.out': mdiPhoneOutgoing,
    'call.other': mdiPhoneOutline,
};

export function referenceIcon(view: FileCabinetViewKey): string {
    return REFERENCE_ICONS[view] ?? mdiFileDocumentOutline;
}

/** How a record that carries a document reads in a list of references. */
export function referenceLabel(reference: FileCabinetReference): string {
    switch (reference.view) {
        case 'mail.in': {
            const mail = reference.record as CabinetMail;
            return mail.barcode ? `Received mail ${mail.barcode}` : 'Received mail';
        }
        case 'mail.out': {
            const to = (reference.record as CabinetLetter).response?.to;
            return to?.company || to?.name ? `Letter to ${to?.company || to?.name}` : 'Letter';
        }
        case 'fax.in': {
            const fax = reference.record as CabinetReceivedFax;
            return fax.fromE164 ? `Fax from ${phone(fax.fromE164)}` : 'Received fax';
        }
        case 'fax.out': {
            const fax = reference.record as CabinetSentFax;
            return fax.toE164 ? `Fax to ${phone(fax.toE164)}` : 'Sent fax';
        }
        case 'email.out': {
            const email = reference.record as CabinetEmail;
            return email.subject ? `Email: ${email.subject}` : 'Email';
        }
        default: {
            const call = reference.record as CabinetCall;
            return [call.method ? `${methodLabel(call.method)} call` : 'Call', call.phoneE164 ? phone(call.phoneE164) : undefined]
                .filter(Boolean)
                .join(' ');
        }
    }
}

/**
 * "Attached to": the records that carry a document. While loading, "…"; when every list answered and
 * none carries it, "None"; when a list failed or was cut short and none was found, unknown — never
 * "None" on partial evidence (NS-3).
 */
const AttachedCell = observer(function AttachedCell({
    documentId,
    getAttachments,
}: {
    documentId?: string;
    getAttachments?: () => FileCabinetAttachments | undefined;
}): React.ReactElement {
    const attachments = getAttachments?.();
    if (!documentId || !attachments || !attachments.documentIds.includes(documentId)) {
        return <Absent />;
    }
    if (attachments.loading) {
        return <S.Absent title="Checking mail, letters, faxes, emails and calls">…</S.Absent>;
    }
    const references = attachments.byDocument[documentId] ?? [];
    if (references.length === 0) {
        return attachments.complete ? (
            // AI calls cannot be searched by document; the drawer's "Referenced by" says so, once.
            <S.Absent title="No mail, letter, fax, email or call carries this document.">None found</S.Absent>
        ) : (
            <S.Absent title="Not known: a list could not be read in full. Open the document for details.">{ABSENT}</S.Absent>
        );
    }
    const label = references.length === 1 ? referenceLabel(references[0]) : `${references.length} records`;
    const names = references.map(referenceLabel);
    const more = attachments.complete ? undefined : 'A list could not be read in full, so there may be more.';
    return (
        <S.AttachedList title={[...names, ...(more ? ['', more] : [])].join('\n')}>
            {references.slice(0, 3).map(reference => (
                <Icon key={`${reference.view}:${reference.id}`} path={referenceIcon(reference.view)} size={0.65} aria-hidden />
            ))}
            <S.Muted>{label}</S.Muted>
            {more && <S.Absent aria-label="there may be more">+</S.Absent>}
            {/* The icons are decoration; a single record's label already names it, several are listed here. */}
            {references.length > 1 && <S.VisuallyHidden>: {names.join(', ')}</S.VisuallyHidden>}
        </S.AttachedList>
    );
});

/**
 * `success` is the API's boolean; an absent one is unknown, not failed (NS-3). `result` is a free
 * string, shown as sent.
 */
export function FaxResult({ fax }: { fax: CabinetSentFax }): React.ReactElement {
    const attempts = fax.attempts ? `${fax.attempts} attempt${fax.attempts > 1 ? 's' : ''}` : undefined;
    const detail = [attempts, fax.retriedFaxId ? 'retry' : undefined].filter(Boolean).join(' · ');
    const tag =
        fax.success === true ? (
            <S.Chip tone="success">Success</S.Chip>
        ) : fax.success === false ? (
            <S.Chip tone="danger">Failed</S.Chip>
        ) : (
            <S.Chip tone="neutral">Unknown</S.Chip>
        );
    return (
        <S.Two>
            <span>{tag}</span>
            {detail && <S.Secondary>{detail}</S.Secondary>}
        </S.Two>
    );
}
